import { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { api, apiFetch, apiUpload } from '../../api/client';
import type { LicenseBusinessProfile, UpdateLicenseBusinessProfileInput } from '../../api/types';
import { Alert, Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';

interface Props {
  licenseId: number;
  isDemo: boolean;
}

type FormState = UpdateLicenseBusinessProfileInput;

const EMPTY_FORM: FormState = {
  business_nif: '',
  business_name: '',
  business_entity_type: 'juridica',
  business_first_name: '',
  business_last_name_1: '',
  business_last_name_2: '',
  business_address: '',
  business_postal_code: '',
  business_city: '',
  business_province: '',
  business_phone: '',
  business_email: '',
  business_default_tax_rate: 10,
};

export function LicenseBusinessProfilePanel({ licenseId, isDemo }: Props) {
  const fieldId = useId();
  const [profile, setProfile] = useState<LicenseBusinessProfile | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.get<LicenseBusinessProfile>(
        `/api/license/${licenseId}/business-profile`
      );
      setProfile(data);
      setForm({
        business_nif: data.business_nif ?? '',
        business_name: data.business_name ?? '',
        business_entity_type: data.business_entity_type,
        business_first_name: data.business_first_name ?? '',
        business_last_name_1: data.business_last_name_1 ?? '',
        business_last_name_2: data.business_last_name_2 ?? '',
        business_address: data.business_address ?? '',
        business_postal_code: data.business_postal_code ?? '',
        business_city: data.business_city ?? '',
        business_province: data.business_province ?? '',
        business_phone: data.business_phone ?? '',
        business_email: data.business_email ?? '',
        business_default_tax_rate: data.business_default_tax_rate ?? 10,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los datos del negocio');
    } finally {
      setLoading(false);
    }
  }, [licenseId]);

  useEffect(() => {
    void load();
  }, [load]);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (isDemo || busy) return;
    setBusy(true);
    setMessage('');
    setError('');
    setWarnings([]);
    try {
      const result = await api.patch<LicenseBusinessProfile & { message?: string; address_warnings?: string[] }>(
        `/api/license/${licenseId}/business-profile`,
        form
      );
      setProfile(result);
      setForm({
        business_nif: result.business_nif ?? '',
        business_name: result.business_name ?? '',
        business_entity_type: result.business_entity_type,
        business_first_name: result.business_first_name ?? '',
        business_last_name_1: result.business_last_name_1 ?? '',
        business_last_name_2: result.business_last_name_2 ?? '',
        business_address: result.business_address ?? '',
        business_postal_code: result.business_postal_code ?? '',
        business_city: result.business_city ?? '',
        business_province: result.business_province ?? '',
        business_phone: result.business_phone ?? '',
        business_email: result.business_email ?? '',
        business_default_tax_rate: result.business_default_tax_rate ?? 10,
      });
      setMessage(result.message ?? 'Datos del negocio actualizados.');
      if (result.address_warnings?.length) {
        setWarnings(result.address_warnings);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron guardar los datos');
    } finally {
      setBusy(false);
    }
  }

  async function handleLogoUpload(file: File | undefined) {
    if (!file || isDemo || busy) return;
    if (file.size > 1_000_000) {
      setError('El logo no puede superar 1 MB.');
      return;
    }
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const body = new FormData();
      body.append('logo', file);
      const result = await apiUpload<LicenseBusinessProfile & { message?: string }>(
        `/api/license/${licenseId}/menu-logo`,
        body
      );
      setProfile({
        ...result,
        menu_logo_url: result.menu_logo_url ? `${result.menu_logo_url}?v=${Date.now()}` : null,
      });
      setMessage(result.message ?? 'Logo de la carta actualizado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo subir el logo');
    } finally {
      setBusy(false);
    }
  }

  async function handleLogoDelete() {
    if (isDemo || busy) return;
    setBusy(true);
    setMessage('');
    setError('');
    try {
      const result = await apiFetch<LicenseBusinessProfile & { message?: string }>(
        `/api/license/${licenseId}/menu-logo`,
        { method: 'DELETE' }
      );
      setProfile(result);
      setMessage(result.message ?? 'Logo de la carta eliminado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo quitar el logo');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <p className="muted" style={{ marginTop: '1rem' }}>Cargando datos del negocio…</p>;
  }

  const isNaturalPerson = form.business_entity_type === 'fisica';
  const naturalPersonFiscalName = [
    form.business_last_name_1,
    form.business_last_name_2,
    form.business_first_name,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className="license-business-panel"
      style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border, #e5e7eb)' }}
    >
      <div className="license-card__meta" style={{ marginBottom: '0.5rem' }}>
        <h4 style={{ margin: 0 }}>Datos del negocio</h4>
        <Badge tone={profile?.profile_complete ? 'success' : 'warning'}>
          {profile?.profile_complete ? 'Completo' : 'Pendiente'}
        </Badge>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Datos del negocio vinculados a la licencia. El TPV los recibe automáticamente en solo lectura.
      </p>

      {message ? <Alert tone="success">{message}</Alert> : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {warnings.length > 0 ? (
        <Alert tone="warning">
          {warnings.map((warning) => (
            <span key={warning} style={{ display: 'block' }}>
              {warning}
            </span>
          ))}
        </Alert>
      ) : null}

      <div className="menu-logo-field">
        <div className="menu-logo-preview">
          {profile?.menu_logo_url ? (
            <img src={profile.menu_logo_url} alt="Logo actual de la carta" />
          ) : (
            <span>Sin logo</span>
          )}
        </div>
        <div className="menu-logo-field__content">
          <strong>Logo de la carta digital</strong>
          <p className="muted">PNG, JPG o WebP de hasta 1 MB. Se mostrará en la cabecera de esta carta.</p>
          {!isDemo ? (
            <div className="menu-logo-field__actions">
              <label className="ui-btn ui-btn--secondary ui-btn--sm">
                {profile?.menu_logo_url ? 'Cambiar logo' : 'Subir logo'}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  disabled={busy}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.target.value = '';
                    void handleLogoUpload(file);
                  }}
                />
              </label>
              {profile?.menu_logo_url ? (
                <Button type="button" variant="ghost" size="sm" disabled={busy} onClick={() => void handleLogoDelete()}>
                  Quitar logo
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <form onSubmit={(event) => void handleSubmit(event)} className="license-business-form">
        <Select
          id={`${fieldId}-entity-type`}
          label="Tipo de titular fiscal"
          value={form.business_entity_type ?? 'juridica'}
          onChange={(event) =>
            updateField(
              'business_entity_type',
              event.target.value as 'fisica' | 'juridica'
            )
          }
          options={[
            { value: 'fisica', label: 'Persona física / autónomo' },
            { value: 'juridica', label: 'Empresa o entidad' },
          ]}
          disabled={isDemo}
        />
        <Input
          id={`${fieldId}-nif`}
          label="NIF / CIF"
          value={form.business_nif ?? ''}
          onChange={(e) => updateField('business_nif', e.target.value.toUpperCase())}
          placeholder="B12345678"
          disabled={isDemo}
          required
        />
        {isNaturalPerson ? (
          <>
            <Input
              id={`${fieldId}-first-name`}
              label="Nombre"
              value={form.business_first_name ?? ''}
              onChange={(event) => updateField('business_first_name', event.target.value)}
              placeholder="María"
              autoComplete="given-name"
              disabled={isDemo}
              required
            />
            <Input
              id={`${fieldId}-last-name-1`}
              label="Primer apellido"
              value={form.business_last_name_1 ?? ''}
              onChange={(event) => updateField('business_last_name_1', event.target.value)}
              placeholder="García"
              autoComplete="family-name"
              disabled={isDemo}
              required
            />
            <Input
              id={`${fieldId}-last-name-2`}
              label="Segundo apellido"
              value={form.business_last_name_2 ?? ''}
              onChange={(event) => updateField('business_last_name_2', event.target.value)}
              placeholder="López"
              hint="Opcional si fiscalmente solo tienes un apellido."
              disabled={isDemo}
            />
            <p className="license-fiscal-preview" aria-live="polite">
              <strong>Nombre que se validará con AEAT:</strong>{' '}
              {naturalPersonFiscalName || 'Completa el nombre y el primer apellido.'}
            </p>
          </>
        ) : (
          <Input
            id={`${fieldId}-business-name`}
            label="Razón social"
            value={form.business_name ?? ''}
            onChange={(event) => updateField('business_name', event.target.value)}
            placeholder="Mi Bar S.L."
            autoComplete="organization"
            disabled={isDemo}
            required
          />
        )}
        <Input
          id={`${fieldId}-address`}
          label="Dirección"
          value={form.business_address ?? ''}
          onChange={(e) => updateField('business_address', e.target.value)}
          placeholder="Calle Mayor 1"
          disabled={isDemo}
        />
        <Input
          id={`${fieldId}-postal-code`}
          label="Código postal"
          value={form.business_postal_code ?? ''}
          onChange={(e) => updateField('business_postal_code', e.target.value.replace(/\D/g, '').slice(0, 5))}
          placeholder="28001"
          inputMode="numeric"
          disabled={isDemo}
          required
        />
        <Input
          id={`${fieldId}-city`}
          label="Población"
          value={form.business_city ?? ''}
          onChange={(e) => updateField('business_city', e.target.value)}
          placeholder="Madrid"
          disabled={isDemo}
        />
        <Input
          id={`${fieldId}-province`}
          label="Provincia"
          value={form.business_province ?? ''}
          onChange={(e) => updateField('business_province', e.target.value)}
          placeholder="Madrid"
          disabled={isDemo}
        />
        <Input
          id={`${fieldId}-phone`}
          label="Teléfono"
          value={form.business_phone ?? ''}
          onChange={(e) => updateField('business_phone', e.target.value)}
          placeholder="+34 910 000 000"
          disabled={isDemo}
        />
        <Input
          id={`${fieldId}-email`}
          label="Email"
          type="email"
          value={form.business_email ?? ''}
          onChange={(e) => updateField('business_email', e.target.value)}
          placeholder="contacto@restaurante.es"
          disabled={isDemo}
        />
        <Input
          id={`${fieldId}-tax-rate`}
          label="IVA por defecto (%)"
          value={String(form.business_default_tax_rate ?? 10)}
          onChange={(e) => {
            const parsed = parseFloat(e.target.value.replace(',', '.'));
            updateField(
              'business_default_tax_rate',
              Number.isFinite(parsed) ? parsed : 10
            );
          }}
          placeholder="10"
          inputMode="decimal"
          disabled={isDemo}
        />
        {!isDemo ? (
          <div className="license-business-form__submit">
            <Button type="submit" variant="secondary" size="sm" loading={busy} disabled={busy}>
              Guardar datos del negocio
            </Button>
          </div>
        ) : null}
      </form>
    </div>
  );
}
